import { RichTableProps } from './RichTable.types';
import { RichTableToolbar } from './RichTableToolbar/RichTableToolbar';
import { Flexbox, Icon, Pagination, Table, Typography } from '@hiiretail/synergy-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ItemsPerPageSelect,
  PaginationContainer,
  RichTableWrapper,
  TableWrapper,
} from './RichTable.styles';
import { Spinner } from '../Spinner/Spinner';
import { Retry } from '../Retry/Retry';
import { EmptyData } from './EmptyData/EmptyData';
import { AccessControl } from '../AccessControl/AccessControl';
import { TableData } from '@hiiretail/synergy-ui/dist/types/components/surfaces/Table/Table.types';

/**
 * Rich table component
 * Component acts as a wrapper for Synergy-UI <Table/> component
 * Adds a toolbar and encapsulates shared functionality
 **/

export const RichTable = <T extends TableData>({
  id,
  columns,
  data,
  title,
  isLoading,
  fetchError,
  refetch,
  errorSubject,
  searchFieldLabel,
  onSearchChange,
  onRowClick,
  permissions,
  hideSearch,
  createButtonTitle,
  onCreateButtonClick,
  createButtonPermission,
}: RichTableProps<T>): JSX.Element => {
  const skipRowClick = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentItems, setCurrentItems] = useState(data.slice(0, itemsPerPage));

  const pageSizeOptions = useMemo(
    () => [
      { label: '10', value: 10 },
      { label: '20', value: 20 },
      { label: '30', value: 30 },
    ],
    [],
  );

  //  Todo probably controllers should handle pagination

  const onPageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setCurrentItems(data.slice((newPage - 1) * itemsPerPage, newPage * itemsPerPage));
  };

  const onItemsPerPageChange = ({ value }: { value: number }) => {
    setItemsPerPage(value);
  };

  useEffect(() => {
    onPageChange(1);
    setItemsPerPage(pageSizeOptions[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pageSizeOptions]);

  useEffect(() => {
    onPageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage]);

  return (
    <RichTableWrapper container item style={{ height: '100%' }} direction="column">
      <RichTableToolbar
        title={title}
        searchFieldLabel={searchFieldLabel}
        onSearchChange={onSearchChange}
        permissions={permissions}
        hideSearch={hideSearch}
        createButtonTitle={createButtonTitle}
        onCreateButtonClick={onCreateButtonClick}
        createButtonPermission={createButtonPermission}
      />
      <AccessControl
        permission={permissions.list}
        renderIfNoAccess={
          <Flexbox
            container
            direction="column"
            item
            flex="1 1 auto"
            alignItems="center"
            justifyContent="center"
            gutter={2}
          >
            <Flexbox item>
              <Icon.WarningTriangle size="lg" color={() => 'rgb(244, 67, 54)'} />
            </Flexbox>
            <Flexbox item>
              <Typography variant="body2">
                You don't have permissions to view {errorSubject}
              </Typography>
            </Flexbox>
            <Flexbox item>
              <Typography variant="body2">
                Required permissions:{' '}
                <Typography variant="body2" weight="bold">
                  {permissions.list}
                </Typography>
              </Typography>
            </Flexbox>
          </Flexbox>
        }
      >
        {isLoading ? (
          <Spinner />
        ) : fetchError ? (
          <Retry
            text={errorSubject}
            message={
              fetchError.response?.status === 403
                ? "You don't have permissions to access this resource"
                : undefined
            }
            handleClick={refetch && fetchError.response?.status !== 403 ? refetch : undefined}
            size="xlg"
            justifyContent="center"
          />
        ) : data.length ? (
          <TableWrapper
            container
            item
            flex="1 1 auto"
            style={{ overflowY: 'auto', height: '100%' }}
            onClickCapture={(e) => {
              skipRowClick.current = !!(e.target as HTMLElement)
                .closest('button, a, input, select, [role="button"], [role="checkbox"]');
            }}
          >
            <Table
              className={onRowClick ? 'clickable-table' : ''}
              onRowClick={onRowClick && ((row) => !skipRowClick.current && onRowClick(row as T))}
              data={currentItems as TableData[]}
              zebra
              columns={columns}
              id={id}
              sortable
            />
          </TableWrapper>
        ) : (
          <EmptyData searchSubject={errorSubject} />
        )}
      </AccessControl>

      {/*@ts-ignore*/}
      <PaginationContainer container item justifyContent="space-between" alignItems="center">
        <Flexbox item container alignItems="center" flex="0.01 1 auto" gutter={2}>
          <Flexbox item>
            <Typography variant="body2">Items per page: </Typography>
          </Flexbox>
          <Flexbox item>
            <ItemsPerPageSelect
              fullWidth
              name="itemsperpage"
              options={pageSizeOptions}
              isClearable={false}
              initialValue={pageSizeOptions[0]}
              // @ts-ignore
              onChange={onItemsPerPageChange}
              isSearchable={false}
              hideLabel
              label=""
            />
          </Flexbox>
        </Flexbox>
        <Flexbox item>
          <Pagination
            variant="table"
            currentPage={currentPage}
            totalItems={data.length}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
          />
        </Flexbox>
      </PaginationContainer>
    </RichTableWrapper>
  );
};
